import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  CreditCard, 
  Check, 
  Sparkles, 
  Crown,
  Zap,
  Star
} from "lucide-react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const packages = [
  {
    id: "basic",
    name: "Trend Tracker",
    price: 9.99,
    icon: Zap,
    color: "#7C3AED",
    features: [
      "Save unlimited trends",
      "Basic analytics",
      "Email trend alerts",
      "7-day historical data"
    ],
    popular: false
  },
  {
    id: "pro",
    name: "Pro Analyst",
    price: 29.99,
    icon: Star,
    color: "#FF0099",
    features: [
      "Everything in Tracker",
      "30-day historical data",
      "Platform breakdown",
      "Related trends",
      "Affiliate program (10%)",
      "Export reports"
    ],
    popular: true
  },
  {
    id: "premium",
    name: "Agency",
    price: 99.99,
    icon: Crown,
    color: "#CCFF00",
    features: [
      "Everything in Pro",
      "90-day historical data",
      "API access",
      "White-label reports",
      "Priority support",
      "Team seats (5)",
      "Custom alerts"
    ],
    popular: false
  }
];

const PaymentPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const handleCheckout = async (packageId) => {
    setLoading(packageId);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/payments/checkout`,
        {
          package_id: packageId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      const message = error.response?.data?.detail || "Payment failed";
      toast.error(message);
      setLoading(null);
    }
  };

  if (user?.is_premium) {
    return (
      <div className="p-4 md:p-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-16"
        >
          <div className="w-20 h-20 bg-[#CCFF00] mx-auto mb-6 flex items-center justify-center border-2 border-black">
            <Crown className="w-10 h-10" />
          </div>
          <h1 className="text-4xl font-extrabold tracking-tighter mb-4">
            You're a Premium Member!
          </h1>
          <p className="text-gray-500 max-w-md mx-auto mb-8">
            Enjoy all premium features including unlimited sponsored posts, 
            top placement, and cross-platform posting.
          </p>
          <Button
            onClick={() => navigate("/upload")}
            className="bg-black text-white hover:bg-[#CCFF00] hover:text-black border-2 border-black font-bold"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Create Sponsored Content
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <div className="flex items-center justify-center gap-2 mb-2">
          <CreditCard className="w-6 h-6 text-[#FF0099]" />
          <span className="text-sm font-mono uppercase tracking-widest text-gray-500">
            UPGRADE
          </span>
        </div>
        <h1 className="text-4xl md:text-6xl font-extrabold tracking-tighter mb-4">
          Unlock Pro Analytics
        </h1>
        <p className="text-gray-600 max-w-xl mx-auto">
          Get deeper insights, historical data, and earn money with our affiliate program.
          Find trends before they peak.
        </p>
      </motion.div>

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {packages.map((pkg, index) => {
          const Icon = pkg.icon;
          return (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`neo-card relative ${
                pkg.popular ? "md:-mt-4 md:mb-4" : ""
              }`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF0099] text-white px-4 py-1 text-xs font-bold uppercase">
                  Most Popular
                </div>
              )}
              
              <div className="p-6">
                {/* Icon & Name */}
                <div
                  className="w-12 h-12 flex items-center justify-center mb-4"
                  style={{ backgroundColor: pkg.color }}
                >
                  <Icon className={`w-6 h-6 ${pkg.color === "#CCFF00" ? "text-black" : "text-white"}`} />
                </div>
                <h3 className="text-xl font-bold mb-1">{pkg.name}</h3>
                
                {/* Price */}
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-extrabold">${pkg.price}</span>
                  <span className="text-gray-500">/one-time</span>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: pkg.color === "#CCFF00" ? "#000" : pkg.color }} />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button
                  onClick={() => handleCheckout(pkg.id)}
                  disabled={loading === pkg.id}
                  data-testid={`checkout-${pkg.id}`}
                  className={`w-full font-bold py-3 border-2 border-black ${
                    pkg.popular
                      ? "bg-[#FF0099] text-white hover:bg-[#CCFF00] hover:text-black"
                      : "bg-black text-white hover:bg-[#CCFF00] hover:text-black"
                  }`}
                >
                  {loading === pkg.id ? (
                    <div className="w-5 h-5 border-2 border-current border-t-transparent animate-spin rounded-full" />
                  ) : (
                    <>Get {pkg.name}</>
                  )}
                </Button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Trust Badges */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-12 text-center"
      >
        <div className="flex items-center justify-center gap-6 text-gray-400 text-sm">
          <div className="flex items-center gap-2">
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
            </svg>
            <span>Stripe Secure</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5" />
            <span>Money-back Guarantee</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5" />
            <span>Instant Activation</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentPage;
